import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  DollarSign,
  Users,
  Heart,
  BookOpen,
  Shield,
  Star,
  ArrowRight,
  Video,
  Stethoscope,
  Clock,
  Target,
  BarChart3,
  PieChart,
  Activity,
  Award,
  CheckCircle
} from 'lucide-react';

const Impact = () => {
  const impactMetrics = [
    { number: "100,000", label: "Children Enrolled (Year-1 target)", icon: Users, color: "primary" },
    { number: "200+", label: "Provider Schools", icon: BookOpen, color: "accent" },
    { number: "50+", label: "Referral Clinics & Hospitals (for procedures)", icon: Stethoscope, color: "secondary" },
    { number: "< 30 min", label: "Telehealth Response Time (Median)", icon: Clock, color: "primary" },
    { number: "$20-30", label: "Family Savings vs. No Coverage (avg.)", icon: DollarSign, color: "accent" }
  ];

  const realWorldBenefits = [
    {
      category: "Health",
      icon: Heart,
      benefits: [
        "Fewer missed school days",
        "Quicker recovery from malaria, diarrhoea, and respiratory infections",
        "Early detection and prevention of health issues",
        "Improved overall health outcomes"
      ],
      color: "primary"
    },
    {
      category: "Family Finances",
      icon: DollarSign,
      benefits: [
        "Lower out-of-pocket shocks",
        "Fewer high-cost emergencies",
        "Fewer lost workdays for caregivers",
        "Predictable healthcare costs"
      ],
      color: "accent"
    },
    {
      category: "Schools",
      icon: BookOpen,
      benefits: [
        "Higher attendance rates",
        "Better academic performance",
        "Safer hygiene culture",
        "Improved learning environment"
      ],
      color: "secondary"
    },
    {
      category: "Clinics",
      icon: Stethoscope,
      benefits: [
        "Predictable patient volume",
        "Faster reimbursements",
        "Telehealth support for complex cases",
        "Enhanced community trust"
      ],
      color: "primary"
    }
  ];

  const stories = [
    {
      title: "Sakahnyuy Nyuyseni",
      content: "With AMHAS my health is guaranteed.",
      author: "Sakahnyuy Nyuyseni",
      role: "AMHAS Member",
      image: "/Sakahnyuy_Nyuyseni.jpg"
    },
    {
      title: "Sevedzem",
      content: "My name is Sevedzem. I am 13 years old. I am in Form 3 (third year in secondary school). After my enrollment into AMHAS I have no worries about my healthcare again. I can now concentrate on my studies. I urge other students to get themselves enrolled into AMHAS and they will not regret it.",
      author: "Sevedzem",
      role: "AMHAS Member",
      image: "/Sevedzem.mp4",
      isVideo: true
    },
    {
      title: "Nyuyfanla",
      content: "My name is Nyuyfanla. I am 11 years old. I am in Form 1. My enrolment in AMHAS has released my parents from the burden of healthcare worries. I am now a proud Nso [his ethnic group] and an African school child. Thanks to AMHAS.",
      author: "Nyuyfanla",
      role: "AMHAS Member",
      image: "/Nyuyfanla.mp4",
      isVideo: true
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_doctor_Care_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Impact</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Healthier children, stronger schools, resilient communities
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Overview */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Transforming Lives Through Healthcare</h2>
            <p className="section-subtitle">
              AMHAS is creating measurable impact across education, household economics, and community health
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen size={32} className="text-primary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Education Impact</h3>
              <p className="text-gray-600 mb-6">
                Healthy children learn better. Our preventive care approach has led to significant improvements in school attendance and academic performance.
              </p>
              <ul className="text-left space-y-2">
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">25% increase in school attendance</span>
                </li>
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">15% improvement in test scores</span>
                </li>
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">40% reduction in sick days</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <DollarSign size={32} className="text-accent-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Household Economics</h3>
              <p className="text-gray-600 mb-6">
                Families save significantly on healthcare costs while protecting against catastrophic medical expenses.
              </p>
              <ul className="text-left space-y-2">
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">80% reduction in out-of-pocket costs</span>
                </li>
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">60% fewer emergency medical debts</span>
                </li>
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">25% reduction in lost workdays</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={32} className="text-secondary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Community Health</h3>
              <p className="text-gray-600 mb-6">
                Building healthier communities through improved hygiene, water safety, and trust in healthcare systems.
              </p>
              <ul className="text-left space-y-2">
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">90% of children receive annual checkups</span>
                </li>
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">70% improvement in hygiene practices</span>
                </li>
                <li className="flex items-center space-x-2">
                  <TrendingUp size={16} className="text-accent-600" />
                  <span className="text-sm">85% trust in AMHAS urgent care facilities</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Real-World Benefits */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Real-World Benefits</h2>
            <p className="section-subtitle">
              Speak to outcomes across all stakeholders
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {realWorldBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-6 text-center"
                >
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${benefit.color === 'primary' ? 'bg-primary-100' :
                    benefit.color === 'accent' ? 'bg-accent-100' :
                      'bg-secondary-100'
                    }`}>
                    <Icon size={32} className={
                      benefit.color === 'primary' ? 'text-primary-600' :
                        benefit.color === 'accent' ? 'text-accent-600' :
                          'text-secondary-600'
                    } />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{benefit.category}</h3>
                  <ul className="space-y-2 text-left">
                    {benefit.benefits.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-2">
                        <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Metrics Panel */}
      <section className="relative section-padding bg-primary-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_nurse_triage_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Early Targets & Impact Counters</h2>
            <p className="text-xl text-primary-100">
              Dynamic targets that will update as data accrues
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {impactMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <motion.div
                  key={metric.label}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={32} className="text-white" />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold mb-2">{metric.number}</div>
                  <div className="text-primary-100">{metric.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Success Stories</h2>
            <p className="section-subtitle">
              Real stories from families, educators, and schools
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stories.map((story, index) => (
              <motion.div
                key={story.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                {story.image && story.image !== "/api/placeholder/300/200" ? (
                  story.isVideo ? (
                    <video 
                      src={story.image} 
                      className="w-full h-48 object-cover rounded-lg mb-6"
                      controls
                      loop
                      muted
                    />
                  ) : (
                    <img 
                      src={story.image} 
                      alt={story.title}
                      className="w-full h-48 object-cover rounded-lg mb-6"
                    />
                  )
                ) : (
                  <div className="w-full h-48 bg-gray-200 rounded-lg mb-6 flex items-center justify-center">
                    <Users size={48} className="text-gray-400" />
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-4">{story.title}</h3>
                <blockquote className="text-gray-600 mb-6 italic">
                  "{story.content}"
                </blockquote>
                <div>
                  <div className="font-semibold text-gray-900">{story.author}</div>
                  <div className="text-gray-600 text-sm">{story.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Be Part of the Impact</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Join us in creating a healthier future for children across Cameroon & The Gambia.
              Whether you're a parent, educator, school administrator, or investor,
              there's a way for you to contribute to our mission.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/student-enrollment" className="btn-primary">
                Enroll Your Child
              </Link>
              <Link to="/urgent-care" className="btn-secondary">
                Provider With Us
              </Link>
              <Link to="/investors" className="btn-accent">
                Invest in Impact
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Impact;
